import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useToasts } from 'react-toast-notifications';
import ScPageHeader from '../../components/molecules/ScPageHeader';
import Button from '../../components/atoms/Button';
import { ButtonKind } from '../../components/atoms/Button/enums';
import Loader from '../../components/atoms/Loader';
import { LoaderMode } from '../../components/atoms/Loader/enums';
import { IconDownload, IconClose, IconRefresh } from '../../assets/svg/icons';
import useHeaderTitleContext from '../../hooks/useHeaderTitleContext';
import {
  useCreateReport,
  useDeleteReport,
  useDownloadReport,
  useReports,
} from './hooks';
import {
  REPORT_TYPES,
  RANGE_PRESETS,
  RANGE_PRESET_LABELS,
  DEFAULT_RANGE,
  reportTypeById,
} from './consts';
import { ReportManifest } from './types';
import {
  StyledTypeGrid,
  StyledTypeCard,
  StyledTypeIcon,
  StyledTypeTitle,
  StyledTypeDesc,
  StyledConfigPanel,
  StyledConfigHead,
  StyledConfigTitle,
  StyledConfigClose,
  StyledFieldLabel,
  StyledRangeRow,
  StyledRangePill,
  StyledColumnGrid,
  StyledColumnCheck,
  StyledCheckbox,
  StyledColumnLabel,
  StyledConfigFooter,
  StyledConfigHint,
  StyledCustomRange,
  StyledDateField,
  StyledDateInput,
  StyledSectionTitle,
  StyledSectionHint,
  StyledReportList,
  StyledReportRow,
  StyledReportIcon,
  StyledReportBody,
  StyledReportPrimary,
  StyledReportMeta,
  StyledReportRight,
  StyledStatusBadge,
  StyledExpiryText,
  StyledDismiss,
  StyledEmpty,
} from './styles';

const ReportsPage: React.FC = () => {
  const intl = useIntl();
  const { addToast } = useToasts();
  const { setHeaderTitle } = useHeaderTitleContext();
  const { reports, isLoading } = useReports();
  const { createReport, isCreating } = useCreateReport();
  const { getDownloadUrl } = useDownloadReport();
  const { deleteReport } = useDeleteReport();

  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [range, setRange] = useState<string>(DEFAULT_RANGE);
  const [columns, setColumns] = useState<string[]>([]);
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  // Custom range is capped to one month (max 31 inclusive days).
  const MAX_RANGE_DAYS = 31;
  const addDays = (iso: string, n: number) => {
    const d = new Date(`${iso}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + n);
    return d.toISOString().slice(0, 10);
  };
  const customMaxTo = customFrom ? addDays(customFrom, MAX_RANGE_DAYS - 1) : undefined;
  const rangeTooLong =
    !!customFrom &&
    !!customTo &&
    (Date.parse(customTo) - Date.parse(customFrom)) / 86400000 + 1 > MAX_RANGE_DAYS;
  const customInvalid =
    range === 'custom' &&
    (!customFrom || !customTo || customFrom > customTo || rangeTooLong);

  const t = (id: string, defaultMessage: string, values?: Record<string, unknown>): string =>
    intl.formatMessage({ id, defaultMessage }, values) as string;

  useEffect(() => {
    setHeaderTitle(t('mainNavigation.reports', 'Reports'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intl, setHeaderTitle]);

  const selectedDef = selectedType ? reportTypeById(selectedType) : undefined;

  const selectReport = (type: string) => {
    const def = reportTypeById(type);
    setSelectedType(type);
    setRange(DEFAULT_RANGE);
    setColumns(def ? def.columns.map((c) => c.key) : []);
    setCustomFrom('');
    setCustomTo('');
  };

  const toggleColumn = (key: string) =>
    setColumns((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );

  const isExpired = (report: ReportManifest) =>
    report.status === 'ready' && new Date(report.expiresAt).getTime() <= Date.now();

  const derivedStatus = (
    report: ReportManifest
  ): 'processing' | 'ready' | 'failed' | 'expired' =>
    report.status === 'processing'
      ? 'processing'
      : report.status === 'failed'
      ? 'failed'
      : isExpired(report)
      ? 'expired'
      : 'ready';

  const formatDate = (iso?: string) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return Number.isNaN(d.getTime())
      ? iso
      : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
  };

  const expiryLabel = (report: ReportManifest) => {
    const ms = new Date(report.expiresAt).getTime() - Date.now();
    if (ms <= 0) return t('reports.linkExpired', 'Download link has expired');
    const days = Math.floor(ms / 86400000);
    const hours = Math.floor((ms % 86400000) / 3600000);
    return t('reports.expiresIn', 'Expires in {days}d {hours}h', { days, hours });
  };

  const generate = async (
    reportType: string,
    rangePreset: string,
    cols?: string[],
    dates?: { fromDate: string; toDate: string }
  ) => {
    try {
      const manifest = await createReport({ reportType, rangePreset, columns: cols, ...dates });
      if (manifest.status === 'failed') {
        addToast(t('reports.generateFailed', 'Could not generate the report. Please try again.'), {
          appearance: 'error',
        });
      } else {
        // Async: generation runs in the background; the report appears below and
        // flips to Ready on its own (the list polls while it's processing).
        addToast(
          t(
            'reports.generatingStarted',
            'Your report is generating — it will appear below when it is ready.'
          ),
          { appearance: 'success' }
        );
      }
    } catch (error) {
      addToast(t('reports.generateFailed', 'Could not generate the report. Please try again.'), {
        appearance: 'error',
      });
    }
  };

  const generateSelected = () => {
    if (!selectedType || columns.length === 0 || customInvalid) return;
    if (range === 'custom') {
      generate(selectedType, 'custom', columns, { fromDate: customFrom, toDate: customTo });
      return;
    }
    generate(selectedType, range, columns);
  };

  const download = async (report: ReportManifest) => {
    try {
      const url = await getDownloadUrl(report.id);
      window.open(url, '_blank', 'noopener');
    } catch (error) {
      addToast(t('reports.downloadFailed', 'Could not open the report. It may have expired.'), {
        appearance: 'error',
      });
    }
  };

  const dismiss = async (report: ReportManifest) => {
    try {
      await deleteReport(report.id);
    } catch (error) {
      addToast(t('reports.dismissFailed', 'Could not remove the report.'), { appearance: 'error' });
    }
  };

  const rowMeta = (report: ReportManifest) => {
    const preset = RANGE_PRESET_LABELS[report.rangePreset];
    const rangeLabel =
      report.rangePreset === 'custom' && report.fromDate && report.toDate
        ? `${formatDate(report.fromDate)} – ${formatDate(report.toDate)}`
        : preset
        ? intl.formatMessage(preset)
        : report.rangePreset;
    return t('reports.rowMeta', '{range} · {format} · {date} · {columns} columns', {
      range: rangeLabel,
      format: (report.format || 'csv').toUpperCase(),
      date: formatDate(report.createdAt),
      columns: report.columns,
    });
  };

  return (
    <>
      <ScPageHeader
        title={t('mainNavigation.reports', 'Reports')}
        subtitle={t(
          'reports.subtitle',
          'Generate downloadable reports of your store activity. Reports run in the background and links stay available for 7 days.'
        )}
      />

      <StyledTypeGrid data-tour="reports-types">
        {REPORT_TYPES.map((def) => {
          const Icon = def.icon;
          return (
            <StyledTypeCard
              key={def.type}
              type="button"
              $selected={selectedType === def.type}
              aria-pressed={selectedType === def.type}
              onClick={() => selectReport(def.type)}
            >
              <StyledTypeIcon $accent={def.accent}>
                <Icon />
              </StyledTypeIcon>
              <StyledTypeTitle>{t(def.titleId, def.defaultTitle)}</StyledTypeTitle>
              <StyledTypeDesc>{t(def.descId, def.defaultDesc)}</StyledTypeDesc>
            </StyledTypeCard>
          );
        })}
      </StyledTypeGrid>

      {selectedDef && (
        <StyledConfigPanel data-tour="reports-config">
          <StyledConfigHead>
            <StyledConfigTitle>
              {t('reports.configure', 'Configure: {name}', {
                name: t(selectedDef.titleId, selectedDef.defaultTitle),
              })}
            </StyledConfigTitle>
            <StyledConfigClose type="button" onClick={() => setSelectedType(null)}>
              {t('reports.close', 'Close')}
            </StyledConfigClose>
          </StyledConfigHead>

          <StyledFieldLabel>{t('reports.dateRange', 'Date range')}</StyledFieldLabel>
          <StyledRangeRow>
            {RANGE_PRESETS.map((preset) => (
              <StyledRangePill
                key={preset}
                type="button"
                $active={range === preset}
                aria-pressed={range === preset}
                onClick={() => setRange(preset)}
              >
                {intl.formatMessage(RANGE_PRESET_LABELS[preset])}
              </StyledRangePill>
            ))}
          </StyledRangeRow>

          {range === 'custom' && (
            <StyledCustomRange>
              <StyledDateField>
                {t('reports.from', 'From')}
                <StyledDateInput
                  type="date"
                  value={customFrom}
                  max={customTo || undefined}
                  onChange={(e) => setCustomFrom(e.target.value)}
                />
              </StyledDateField>
              <StyledDateField>
                {t('reports.to', 'To')}
                <StyledDateInput
                  type="date"
                  value={customTo}
                  min={customFrom || undefined}
                  max={customMaxTo}
                  onChange={(e) => setCustomTo(e.target.value)}
                />
              </StyledDateField>
              <StyledConfigHint>{t('reports.maxOneMonth', 'Up to one month')}</StyledConfigHint>
            </StyledCustomRange>
          )}

          <StyledFieldLabel>
            {t('reports.columnsToInclude', 'Columns to include')}
          </StyledFieldLabel>
          <StyledColumnGrid>
            {selectedDef.columns.map((col) => {
              const checked = columns.includes(col.key);
              return (
                <StyledColumnCheck
                  key={col.key}
                  type="button"
                  $checked={checked}
                  aria-pressed={checked}
                  onClick={() => toggleColumn(col.key)}
                >
                  <StyledCheckbox $checked={checked} aria-hidden="true" />
                  <StyledColumnLabel>{t(col.labelId, col.defaultLabel)}</StyledColumnLabel>
                </StyledColumnCheck>
              );
            })}
          </StyledColumnGrid>

          <StyledConfigFooter>
            <StyledConfigHint>
              {t('reports.backgroundHint', 'Runs in the background — you can keep working.')}
            </StyledConfigHint>
            <Button
              kind={ButtonKind.Primary}
              onClick={generateSelected}
              disabled={isCreating || columns.length === 0 || customInvalid}
            >
              {isCreating
                ? t('reports.generating', 'Generating…')
                : t('reports.generateReport', 'Generate report')}
            </Button>
          </StyledConfigFooter>
        </StyledConfigPanel>
      )}

      <StyledSectionTitle data-tour="reports-recent">
        {t('reports.recent', 'Recent reports')}
        <StyledSectionHint>{t('reports.recentHint', 'Links are valid for 7 days')}</StyledSectionHint>
      </StyledSectionTitle>

      {isLoading ? (
        <Loader mode={LoaderMode.WarehouseReport} />
      ) : reports.length === 0 ? (
        <StyledEmpty>{t('reports.empty', 'No reports yet. Pick a report above to generate one.')}</StyledEmpty>
      ) : (
        <StyledReportList>
          {reports.map((report) => {
            const def = reportTypeById(report.reportType);
            const Icon = def?.icon;
            const status = derivedStatus(report);
            return (
              <StyledReportRow key={report.id}>
                {Icon && (
                  <StyledReportIcon $accent={def?.accent ?? 'blue'}>
                    <Icon />
                  </StyledReportIcon>
                )}
                <StyledReportBody>
                  <StyledReportPrimary>
                    {def ? t(def.titleId, def.defaultTitle) : report.reportType}
                  </StyledReportPrimary>
                  <StyledReportMeta>{rowMeta(report)}</StyledReportMeta>
                </StyledReportBody>

                <StyledReportRight>
                  <StyledStatusBadge $status={status}>
                    {status === 'processing' && t('reports.status.processing', 'Generating…')}
                    {status === 'ready' && t('reports.status.ready', 'Ready')}
                    {status === 'failed' && t('reports.status.failed', 'Failed')}
                    {status === 'expired' && t('reports.status.expired', 'Expired')}
                  </StyledStatusBadge>

                  <StyledExpiryText>
                    {status === 'processing'
                      ? t('reports.inProgress', 'Working on it — this can take a moment.')
                      : status === 'failed'
                      ? report.errorText || t('reports.exportFailed', 'Export failed')
                      : expiryLabel(report)}
                  </StyledExpiryText>

                  {status === 'ready' && (
                    <Button kind={ButtonKind.Primary} icon={IconDownload} onClick={() => download(report)}>
                      {t('reports.downloadCsv', 'Download CSV')}
                    </Button>
                  )}
                  {status === 'failed' && (
                    <Button
                      kind={ButtonKind.Outline}
                      icon={IconRefresh}
                      disabled={isCreating}
                      onClick={() => generate(report.reportType, report.rangePreset)}
                    >
                      {t('reports.retry', 'Retry')}
                    </Button>
                  )}
                  {status === 'expired' && (
                    <Button
                      kind={ButtonKind.Outline}
                      icon={IconRefresh}
                      disabled={isCreating}
                      onClick={() => generate(report.reportType, report.rangePreset)}
                    >
                      {t('reports.regenerate', 'Re-generate')}
                    </Button>
                  )}

                  <StyledDismiss
                    type="button"
                    aria-label={t('reports.dismiss', 'Dismiss report')}
                    onClick={() => dismiss(report)}
                  >
                    <IconClose />
                  </StyledDismiss>
                </StyledReportRight>
              </StyledReportRow>
            );
          })}
        </StyledReportList>
      )}
    </>
  );
};

export default ReportsPage;
